import {WizardStepConfig} from 'platform/components';

import {ReactElement} from 'react';
import {Control, useWatch} from 'react-hook-form';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  RentalVehicle,
  useBaseVehicleWizard,
  useDebouncedWatch,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';

export const useRentalVehicleWizard = (
  control: Control<RentalVehicle>,
  stepContents: {
    basicInformation: ReactElement;
    vehicleData: ReactElement;
    features: ReactElement;
    serviceIntervals: ReactElement;
    rentalInformation: ReactElement;
  }
): WizardStepConfig[] => {
  const wizardSteps = useBaseVehicleWizard({
    basicInformation: stepContents.basicInformation,
    vehicleData: stepContents.vehicleData,
    features: stepContents.features,
  });

  // Watch rental properties immediately (dropdown selections)
  const [rentalPriceGroupItemId, carParkId] = useWatch({
    control,
    name: [
      'rentalProperties.rentalPriceGroup.rentalPriceGroupItemId',
      'rentalProperties.carParkId',
    ],
  });

  // Debounce serviceIntervals to reduce performance impact when service interval fields change
  const serviceIntervals = useDebouncedWatch(control, ['serviceIntervals'], 300)[0];

  const isRentalPriceGroupItemIdFilled =
    isNotNilOrEmpty(rentalPriceGroupItemId) && isNotNilOrEmpty(carParkId);
  const areServiceIntervalsFilled = Object.values(serviceIntervals ?? {})
    // @ts-expect-error
    .flatMap((item) => Object.values(item))
    .some((item) => isNotNilOrEmpty(item));

  return [
    ...wizardSteps,
    {
      title: i18n.t('entity.vehicle.labels.serviceIntervals'),
      content: stepContents.serviceIntervals,
      subtitle: areServiceIntervalsFilled ? undefined : i18n.t('general.labels.notSpecified'),
      isBold: true,
    },
    {
      title: i18n.t('entity.vehicle.labels.rentalInformation'),
      content: stepContents.rentalInformation,
      severity: isRentalPriceGroupItemIdFilled ? 'success' : 'warning',
      subtitle: isRentalPriceGroupItemIdFilled ? undefined : i18n.t('general.labels.notSpecified'),
      isBold: true,
    },
  ];
};
